import type { CountryCode } from "libphonenumber-js/min";
import { AsYouType, isSupportedCountry, parsePhoneNumberFromString } from "libphonenumber-js/min";

// the stored country is free text, so only a real ISO alpha 2 code becomes a parse region
const toCountryCode = (country?: string | null): CountryCode | undefined => {
  const normalized = country?.toUpperCase();
  return normalized && isSupportedCountry(normalized) ? normalized : undefined;
};

/**
 * formats a phone number in international style like +64 21 234 5678
 *
 * country is the parse region for numbers stored without a country code and an unknown region is ignored
 * anything that does not parse as a valid number comes back untouched so nothing stored ever disappears
 */
export const formatPhoneNumber = (phoneNumber: string, country?: string | null): string => {
  const parsed = parsePhoneNumberFromString(phoneNumber, {
    defaultCountry: toCountryCode(country),
    extract: false,
  });
  if (!parsed?.isValid()) {
    return phoneNumber;
  }
  return parsed.formatInternational();
};

/**
 * spaces the digits of a phone number the way the given country writes them locally while they are being typed, like 201 8 then 201 867 3392 in the US or 020 7 then 020 7946 0958 in the UK
 *
 * this is a display template over the raw digits, nothing is validated and no code inside the digits is consulted, so a US address groups whatever was typed by the US pattern rather than reading 44 as the UK
 * the country's own punctuation like the US parens and dashes becomes plain spaces so every country reads the same way
 * digits that overflow the country's template come back ungrouped and without a known country the value comes back untouched
 */
export const formatPhoneNumberAsTyped = (phoneNumber: string, country?: string | null): string => {
  const countryCode = toCountryCode(country);
  if (!countryCode) {
    return phoneNumber;
  }
  const digits = phoneNumber.replace(/\D/g, "");
  const templated = new AsYouType(countryCode).input(digits);
  return templated.replace(/\D+/g, " ").trim();
};
